import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Office Cleaning',
    'Medical Centre Cleaning',
    'Gym & Leisure Cleaning',
    'Retail Cleaning',
    'Hospitality Staffing',
    'Property Turnovers',
    'Waste & Hygiene Management',
    'Other',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service_type || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We\'ll be in touch shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_type: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-white" data-testid="footer">
      {/* Contact Form Section */}
      <div className="section-padding border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className="font-oswald text-3xl md:text-4xl font-bold text-slate-800 uppercase tracking-tight mb-6"
                data-testid="contact-headline"
              >
                Start a <span className="text-accent">Conversation</span>
              </h2>
              <p className="font-manrope text-lg text-slate-600 mb-8">
                Whether you're exploring options or ready to discuss a specific requirement, 
                we'll respond within 24 hours with a clear next step.
              </p>

              {/* Contact Info */}
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-light rounded-sm flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-manrope text-sm text-slate-500 uppercase tracking-wider mb-1">
                      Email
                    </p>
                    <a
                      href="mailto:info@hatimiri.com"
                      className="font-manrope text-base text-slate-800 hover:text-accent transition-colors glow-text"
                      data-testid="footer-email"
                    >
                      info@hatimiri.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-light rounded-sm flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-manrope text-sm text-slate-500 uppercase tracking-wider mb-1">
                      Phone
                    </p>
                    <a
                      href="tel:+447549328582"
                      className="font-manrope text-base text-slate-800 hover:text-accent transition-colors glow-text"
                      data-testid="footer-phone"
                    >
                      +44 7549 328 582
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-light rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-manrope text-sm text-slate-500 uppercase tracking-wider mb-1">
                      Address
                    </p>
                    <span className="font-manrope text-base text-slate-800" data-testid="footer-address">
                      5 Brayford Square, London,<br />England, E1 0SG
                    </span>
                  </div>
                </div>
              </div>

              {/* Slogan */}
              <div className="mt-12 p-6 bg-slate-100 rounded-sm border-l-4 border-accent">
                <p className="font-oswald text-2xl font-bold text-slate-800 uppercase tracking-tight">
                  "We Don't <span className="text-accent">Stop.</span>"
                </p>
              </div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6 bg-slate-50 p-8 rounded-sm border border-slate-200" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                      Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 h-12 rounded-sm focus:border-accent"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                      Email *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 h-12 rounded-sm focus:border-accent"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                      Phone
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+44 123 456 7890"
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 h-12 rounded-sm focus:border-accent"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                      Company
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Company name"
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 h-12 rounded-sm focus:border-accent"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                    Service Required *
                  </label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                  >
                    <SelectTrigger 
                      className="bg-white border-slate-300 text-slate-800 h-12 rounded-sm focus:border-accent"
                      data-testid="contact-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {serviceOptions.map((service) => (
                        <SelectItem
                          key={service}
                          value={service}
                          className="text-slate-800 hover:bg-slate-100 focus:bg-slate-100"
                        >
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="font-manrope text-sm font-semibold text-slate-600 uppercase tracking-wider block mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 rounded-sm focus:border-accent resize-none"
                    data-testid="contact-message-textarea"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent-hover text-white font-manrope font-bold text-base py-6 rounded-sm btn-glow uppercase tracking-wider"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Enquiry
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <a
              href="#"
              className="flex items-center gap-2 group"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span className="font-oswald text-xl font-black tracking-tight">
                <span className="text-primary">HATI</span>
                <span className="text-accent">{' '}MIRI</span>
              </span>
              <div className="flex items-center px-2 py-0.5 rounded border border-slate-300 bg-slate-50">
                <span className="font-oswald text-xs font-semibold tracking-[0.12em] uppercase text-slate-600">
                  FM
                </span>
              </div>
            </a>

            {/* Copyright */}
            <p className="font-manrope text-sm text-slate-500 text-center">
              © {new Date().getFullYear()} Hati Miri Facility Management. All rights reserved.
            </p>

            {/* Links */}
            <div className="flex items-center gap-6">
              <a
                href="https://www.hatimiri.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-manrope text-sm text-slate-600 hover:text-accent transition-colors"
                data-testid="footer-website-link"
              >
                www.hatimiri.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
